import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api/api';
import { toast } from 'react-toastify';
import { useAuth } from './auth-context';
import { SERVER_URL } from '../constants/server-type';

export default function InviteAcceptPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null); // { orgId, orgName, email?, rolesAllowed, expiresAt }
  const [tab, setTab] = useState('signup'); // 'signup' | 'signin'
  const [err, setErr] = useState('');

  // Sign up form
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');

  // Sign in form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPass,  setLoginPass]  = useState('');

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setErr('');
      setLoading(true);
      try {
        const res = await apiFetch(`/invites/verify/${token}`, { method: 'GET' });
        const json = res.ok ? await res.json() : null;
        if (!json?.valid) throw new Error(json?.message || 'Invalid or expired invite.');
        if (isMounted) {
          setMeta(json);
          if (json.email) { setEmail(json.email); setLoginEmail(json.email); }
        }
      } catch (e) {
        setErr(e.message || String(e));
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [token]);

  const accept = async () => {
    try {
      const res = await apiFetch('/invites/accept', { method: 'POST', body: { token } });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Unable to accept invite');
      }
      const json = await res.json(); // expect { orgId, accessToken? }
      if (json?.accessToken) localStorage.setItem('accessToken', json.accessToken);
      if (json?.orgId) localStorage.setItem('activeOrgId', json.orgId);
      toast.success(`Joined ${meta?.orgName || 'organization'}`);
      nav('/admin', { replace: true });
    } catch (e) {
      toast.error(e.message || String(e));
    }
  };

  const doSignupAndAccept = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    try {
      // 1) Create user
      const reg = await apiFetch('/auth/register', {
        method: 'POST',
        body: { firstName, lastName, email, password }
      });
      if (!reg.ok) throw new Error(await reg.text() || 'Registration failed');

      // 2) Accept invite
      await accept();
    } catch (e) {
      toast.error(e.message || String(e));
    }
  };

  const doSigninAndAccept = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch('/auth/login', { method: 'POST', body: { email: loginEmail, password: loginPass } });
      if (!res.ok) throw new Error(await res.text() || 'Login failed');
      const json = await res.json();
      if (json?.accessToken) localStorage.setItem('accessToken', json.accessToken);
      await accept();
    } catch (e) {
      toast.error(e.message || String(e));
    }
  };

  const loggedIn = !!user;

  return (
    <div style={wrap}>
      <div style={panel}>
        <h2 style={{marginTop:0}}>Join organization</h2>
        {loading ? <p>Verifying invite…</p> : err ? (
          <div style={{ color:'crimson' }}>{err}</div>
        ) : (
          <>
            <p>You’ve been invited to <b>{meta?.orgName || 'an organization'}</b>.</p>
            {meta?.expiresAt && <p style={{color:'#666'}}>Expires: {new Date(meta.expiresAt).toLocaleString()}</p>}
            {meta?.rolesAllowed?.length ? <p style={{color:'#666'}}>Role hint: {meta.rolesAllowed.join(', ')}</p> : null}

            {loggedIn ? (
              <>
                <div style={{padding:'8px 12px', background:'#f7f7f7', border:'1px solid #eee', borderRadius:8, margin:'12px 0'}}>
                  Signed in. Click accept to join.
                </div>
                <div style={{textAlign:'right'}}>
                  <button onClick={accept} style={btnPrimary}>Accept & Join</button>
                </div>
              </>
            ) : (
              <>
                <div style={{display:'flex', gap:8, margin:'8px 0'}}>
                  <button onClick={()=>setTab('signup')} style={tab === 'signup' ? btnTabActive : btnTab}>Sign up</button>
                  <button onClick={()=>setTab('signin')} style={tab === 'signin' ? btnTabActive : btnTab}>Sign in</button>
                </div>

                {tab === 'signup' ? (
                  <form onSubmit={doSignupAndAccept}>
                    <div style={row}><label style={label}>First name*</label>
                      <input style={input} value={firstName} onChange={e=>setFirstName(e.target.value)} /></div>
                    <div style={row}><label style={label}>Last name*</label>
                      <input style={input} value={lastName} onChange={e=>setLastName(e.target.value)} /></div>
                    <div style={row}><label style={label}>Email*</label>
                      <input style={input} type="email" value={email} onChange={e=>setEmail(e.target.value)} /></div>
                    <div style={row}><label style={label}>Password*</label>
                      <input style={input} type="password" value={password} onChange={e=>setPassword(e.target.value)} /></div>
                    <div style={row}><label style={label}>Confirm*</label>
                      <input style={input} type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} /></div>
                    <div style={{textAlign:'right'}}><button type="submit" style={btnPrimary}>Create account & Join</button></div>
                  </form>
                ) : (
                  <form onSubmit={doSigninAndAccept}>
                    <div style={row}><label style={label}>Email</label>
                      <input style={input} type="email" value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} /></div>
                    <div style={row}><label style={label}>Password</label>
                      <input style={input} type="password" value={loginPass} onChange={e=>setLoginPass(e.target.value)} /></div>
                    <div style={{textAlign:'right'}}><button type="submit" style={btnPrimary}>Sign in & Join</button></div>
                  </form>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const wrap = { display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100vh', background:'#f7f8fb', padding:16 };
const panel = { width:560, maxWidth:'96vw', background:'#fff', border:'1px solid #eee', borderRadius:12, padding:16, boxShadow:'0 10px 30px rgba(0,0,0,.06)' };
const row = { display:'flex', flexDirection:'column', margin:'8px 0' };
const label = { fontSize:13, color:'#555', marginBottom:6 };
const input = { padding:'10px 12px', border:'1px solid #ddd', borderRadius:8 };
const btnPrimary = { border:0, background:'#0f62fe', color:'#fff', borderRadius:8, padding:'10px 14px', cursor:'pointer' };
const btnTab = { border:'1px solid #ddd', background:'#fff', borderRadius:8, padding:'6px 10px', cursor:'pointer' };
const btnTabActive = { ...btnTab, background:'#eef4ff', borderColor:'#cfe0ff' };
